import type { EditorLoadFailure } from "../../types/editorLoadFailure";

export const OOM_DIALOG_TITLE = "Unable to load object";
export const OOM_DIALOG_MESSAGE =
  "The object could not be loaded because memory ran out during initialization. Reload the page to start with a clean editor.";

const OOM_ERROR_PATTERN =
  /out\s*of\s*memory|outofmemory|\boom\b|cannot enlarge memory arrays|could not allocate enough memory|allocate enough memory|memory access out of bounds/i;

interface BuildUnityLoadFailureInput {
  errorMessage?: string | null;
  status?: string | null;
  isRestoreFlow?: boolean;
  requestId?: string | null;
}

export const isOutOfMemoryErrorMessage = (
  errorMessage?: string | null,
): boolean => OOM_ERROR_PATTERN.test(errorMessage ?? "");

export const isModelLoadFailureStatus = (
  status?: string | null,
): boolean =>
  status === "model_load_start_failed" ||
  status === "viewrloader_call_failed" ||
  status === "runtime_error" ||
  status === "startup_error" ||
  status === "create_instance_failed" ||
  status === "iframe_error";

export const buildGeneralLoadFailure = (
  message: string,
  title = "Object load failed",
): EditorLoadFailure => ({
  title,
  kind: "load_failure",
  message,
  blocking: false,
  source: "general",
});

export const buildRestoreLoadFailure = (
  message: string,
  requestId?: string | null,
): EditorLoadFailure => ({
  title: OOM_DIALOG_TITLE,
  kind: "load_failure",
  message,
  blocking: true,
  source: "restore",
  requestId: requestId ?? undefined,
});

export const buildRestoreResetFailure = (
  requestId?: string | null,
  message = "Editor reset timed out before restore could start. Reload the page to start with a clean editor.",
): EditorLoadFailure => ({
  title: "Editor reset failed",
  kind: "restore_reset_failure",
  message,
  blocking: true,
  source: "restore",
  requestId: requestId ?? undefined,
});

export const buildEditorLoadFailureFromUnityError = ({
  errorMessage,
  status,
  isRestoreFlow = false,
  requestId,
}: BuildUnityLoadFailureInput): EditorLoadFailure | null => {
  const normalizedMessage = errorMessage?.trim() ?? "";
  const source = isRestoreFlow ? "restore" : "general";
  const blocking = isRestoreFlow;

  if (isOutOfMemoryErrorMessage(normalizedMessage)) {
    return {
      title: OOM_DIALOG_TITLE,
      kind: "oom",
      message: OOM_DIALOG_MESSAGE,
      blocking,
      source,
      requestId: requestId ?? undefined,
    };
  }

  if (!isModelLoadFailureStatus(status)) {
    return null;
  }

  return {
    title: "Object load failed",
    kind: "load_failure",
    message: `Object load failed: ${normalizedMessage || "Unknown error"}`,
    blocking,
    source,
    requestId: requestId ?? undefined,
  };
};
